'use client';

import { useEffect, useRef, useState } from 'react';

import { SCREEN_SM } from '@/shared/constants/common';

export default function CaseBar({
  barBg,
  barImg,
  mobileBarBg,
}: {
  barBg: string;
  barImg: string;
  mobileBarBg?: string;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const barContainerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setStartX(e.pageX - (scrollRef.current?.offsetLeft || 0));
    setScrollLeft(scrollRef.current?.scrollLeft || 0);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging || !scrollRef.current) return;
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 1.5;
    scrollRef.current.scrollLeft = scrollLeft - walk;
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    } else {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging, startX, scrollLeft]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.innerWidth > SCREEN_SM) return;
    const el = scrollRef.current;
    const container = barContainerRef.current;
    if (!el || !container) return;
    if (el.scrollWidth <= el.clientWidth) return;

    const observer = new window.IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && entry.intersectionRatio === 1) {
          const initial = el.scrollLeft;
          const maxScroll = Math.min(60, el.scrollWidth - el.clientWidth);
          el.scrollTo({ left: initial + maxScroll, behavior: 'smooth' });
          setTimeout(() => {
            el.scrollTo({ left: initial, behavior: 'smooth' });
          }, 600);
        }
      },
      { threshold: 1 },
    );
    observer.observe(container);

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <div
        ref={barContainerRef}
        className="rounded-20 relative mt-9 overflow-hidden"
      >
        <img
          src={barBg}
          alt="bar_bars_bg"
          className="rounded-20 mb-9 h-[807px] w-[auto] max-w-none max-sm:hidden"
        />
        <img
          src={mobileBarBg}
          alt="mobile_bars_bg"
          className="rounded-20 mb-9 hidden h-[612px] w-[auto] max-w-none max-sm:block"
        />
        <div className="absolute top-[118px] flex w-full max-sm:top-[82px]">
          <div className="ml-[110px] max-sm:ml-[60px]" />
          <div
            ref={scrollRef}
            onMouseDown={handleMouseDown}
            className="custom-green-scrollbar w-full cursor-grab overflow-x-scroll active:cursor-grabbing"
          >
            <div className="min-w-[1024px] max-sm:min-w-[734px]">
              <img
                src={barImg}
                alt="bar_bars"
                className="rounded-20 mt-9 mb-2 h-[610px] object-cover opacity-[0.8] max-sm:mb-5 max-sm:h-[440px]"
                draggable={false}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
